import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import ApporteursInsights from "@/components/ApporteursInsights";
import ApporteurSegmentationEditor from "@/components/ApporteurSegmentationEditor";
import { api, formatEUR } from "@/lib/api";
import { PartnerLinkBlock } from "@/components/PartnerLinkBlock";
import { toast } from "sonner";
import {
  Crown, TrendUp, X, ArrowRight, UsersThree, ArrowClockwise, Warning, Plus, MagnifyingGlass,
  Briefcase, User as UserIcon, Question, PencilSimple, Trash, ClockClockwise,
} from "@phosphor-icons/react";
import {
  BUSINESS_ACTIVITIES,
  BUSINESS_ACTIVITY_LABELS,
  PERSONAL_RELATION_LABELS,
  categoryBadgeStyle,
} from "@/lib/apporteurSegmentation";

/* ---------- Helpers ---------- */
function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  return `${v.toFixed(1).replace(/\.0$/, "")}%`;
}
function fmtInt(v) {
  if (v === null || v === undefined) return "—";
  return v.toString();
}
function fmtMoney(v) {
  if (v === null || v === undefined) return "—";
  return formatEUR(v);
}

/* ---------- Sort selector ---------- */
const SORTS = [
  { key: "volume", label: "Volume" },
  { key: "closing", label: "Closing" },
  { key: "ca_net", label: "CA net" },
  { key: "tier", label: "Tier" },
  { key: "retard", label: "Retard" },
];

const TIER_STYLES = {
  A: { bg: "var(--cf-success-soft)", color: "var(--cf-success-text)", border: "var(--cf-success-border)", label: "A" },
  B: { bg: "var(--cf-warning-soft)", color: "var(--cf-warning-text)", border: "var(--cf-warning-border)", label: "B" },
  C: { bg: "var(--cf-danger-soft)", color: "var(--cf-danger-text)", border: "var(--cf-danger-border)", label: "C" },
};

function TierBadge({ tier }) {
  const s = TIER_STYLES[tier] || TIER_STYLES.C;
  return (
    <span
      className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-extrabold border"
      style={{ background: s.bg, color: s.color, borderColor: s.border }}
      title={`Tier ${s.label}`}
      data-testid={`apporteur-tier-${tier}`}
    >
      {s.label}
    </span>
  );
}

/* V4.43 — Badge catégorie (PRO / PARTICULIER / NON RENSEIGNÉ) */
function CategoryBadge({ category }) {
  const s = categoryBadgeStyle(category);
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[8.5px] font-extrabold uppercase tracking-wider border"
      style={{ background: s.bg, color: s.color, borderColor: s.border }}
      title={`Catégorie : ${s.label}`}
      data-testid={`apporteur-category-${category || "non_renseigne"}`}
    >
      {s.label}
    </span>
  );
}

function fmtRelativeDate(iso, sourceLabel = false) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((today - d) / 86_400_000);
    let txt;
    if (diff === 0) txt = "aujourd'hui";
    else if (diff === 1) txt = "hier";
    else if (diff > 0 && diff < 7) txt = `il y a ${diff}j`;
    else if (diff > 0 && diff < 30) txt = `il y a ${Math.round(diff / 7)}sem`;
    else if (diff > 0) txt = `il y a ${Math.round(diff / 30)}mo`;
    else txt = `dans ${-diff}j`;
    return txt;
  } catch {
    return iso;
  }
}

function fmtRelance(daysUntil, late) {
  if (daysUntil === null || daysUntil === undefined) return "—";
  if (late) return `en retard ${-daysUntil}j`;
  if (daysUntil === 0) return "aujourd'hui";
  if (daysUntil === 1) return "demain";
  return `dans ${daysUntil}j`;
}

/* ---------- Podium card (Top 5) ---------- */
function PodiumCard({ title, icon: Icon, color, items, metric, metricFmt = fmtInt }) {
  return (
    <div className="card-base p-4" data-testid={`podium-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <h3 className="flex items-center gap-1.5 font-display text-[13px] font-extrabold uppercase tracking-wider text-slate-900 mb-3">
        <Icon size={14} weight="fill" color={color} /> {title}
      </h3>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-6">Aucune donnée</div>
      ) : (
        <ol className="space-y-1.5">
          {items.map((a, i) => (
            <li
              key={a.name}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50"
              data-testid={`podium-row-${i}`}
            >
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold flex-shrink-0"
                style={{ background: i === 0 ? "var(--cf-warning-soft)" : "var(--cf-bg-2)", color: i === 0 ? "var(--cf-warning-text)" : "var(--cf-neutral-text)" }}
              >
                {i + 1}
              </span>
              <span className="flex-1 truncate text-[13px] font-bold text-slate-800">{a.name}</span>
              <span className="text-[13px] font-extrabold tabular-nums" style={{ color }}>
                {metricFmt(a[metric])}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/* ---------- Parrain inline editor ---------- */
function ParrainInline({ currentParrain, apporteurName, options, onSaved, onNavigate }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentParrain || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(currentParrain || ""); }, [currentParrain]);

  const save = async () => {
    const trimmed = value.trim();
    if (trimmed.toLowerCase() === (apporteurName || "").toLowerCase()) {
      toast.error("Un apporteur ne peut pas être son propre parrain");
      return;
    }
    setSaving(true);
    try {
      // POST /apporteurs est idempotent (upsert) — sert aussi à modifier.
      await api.post("/apporteurs", {
        name: apporteurName,
        parrain: trimmed || null,
        commission_default: 500.0,
      });
      toast.success(trimmed ? `Parrain défini : ${trimmed}` : "Parrain retiré");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="mt-1 flex items-center gap-1.5" data-testid="parrain-editor">
        <input
          type="text"
          list="parrain-options"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Aucun parrain"
          autoFocus
          className="text-[12px] font-semibold border border-[color:var(--cf-primary)] rounded px-2 py-1 text-slate-800 bg-white outline-none flex-1 min-w-0"
          data-testid="parrain-input"
        />
        <datalist id="parrain-options">
          {options.filter((n) => n.toLowerCase() !== (apporteurName || "").toLowerCase()).map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="text-[11px] font-bold text-white bg-[color:var(--cf-primary)] rounded px-2 py-1 hover:bg-[color:var(--cf-primary-hover)] disabled:opacity-50"
          data-testid="parrain-save"
        >
          {saving ? "…" : "OK"}
        </button>
        <button
          type="button"
          onClick={() => { setValue(currentParrain || ""); setEditing(false); }}
          className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:bg-slate-100"
          data-testid="parrain-cancel"
        >
          <X size={11} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <div className="text-[11.5px] text-slate-500 mt-0.5 font-semibold flex items-center gap-1 flex-wrap" data-testid="parrain-display">
      <span>Parrain :</span>
      {currentParrain ? (
        <button
          type="button"
          onClick={() => onNavigate?.(currentParrain)}
          className="text-slate-700 font-bold underline decoration-dotted underline-offset-2 hover:text-[color:var(--cf-primary)]"
          data-testid="parrain-navigate"
          title="Ouvrir la fiche du parrain"
        >
          {currentParrain}
        </button>
      ) : (
        <span className="text-slate-400 italic">Aucun parrain</span>
      )}
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-[10.5px] font-bold text-[color:var(--cf-primary)] hover:underline ml-1"
        data-testid="parrain-edit"
      >
        Modifier
      </button>
    </div>
  );
}

/* ---------- Drawer (mobile-friendly) ---------- */
function ApporteurDrawer({ apporteur, onClose, onContactStamped, onSwitchApporteur, onRenamed, allApporteurs }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  // V4.47 — Rename + Delete + Activity log
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [activity, setActivity] = useState([]);
  const navigate = useNavigate();
  const name = apporteur?.name;

  const reloadMeta = async () => {
    if (!name) return;
    try {
      const reg = await api.get("/apporteurs/registry");
      const allRegistered = reg.data?.registered || [];
      const meta = allRegistered.find((r) => r.name_key === name.toLowerCase()) || null;
      setData((prev) => prev ? { ...prev, meta } : prev);
    } catch { /* noop */ }
  };

  // V4.47 — Charge l'historique d'activité (relances, renames, etc.)
  const reloadActivity = async () => {
    if (!name) return;
    try {
      const r = await api.get(`/apporteurs/${encodeURIComponent(name)}/activity?limit=10`);
      setActivity(r.data?.items || []);
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (!name) return;
    setLoading(true);
    setConfirmDelete(false);
    setRenaming(false);
    (async () => {
      try {
        const [doss, reg, act] = await Promise.all([
          api.get(`/apporteurs/${encodeURIComponent(name)}/dossiers`),
          api.get("/apporteurs/registry"),
          api.get(`/apporteurs/${encodeURIComponent(name)}/activity?limit=10`),
        ]);
        const allRegistered = reg.data?.registered || [];
        const meta = allRegistered.find((r) => r.name_key === name.toLowerCase()) || null;
        setData({ ...doss.data, meta });
        setActivity(act.data?.items || []);
      } catch {
        toast.error("Impossible de charger le détail");
      } finally {
        setLoading(false);
      }
    })();
  }, [name]);

  const markContacted = async () => {
    if (!name) return;
    setMarking(true);
    try {
      const r = await api.post(`/apporteurs/${encodeURIComponent(name)}/mark-contacted`);
      const lastDate = r.data?.last_contact_date || new Date().toISOString().slice(0, 10);
      toast.success(`${name} relancé · dernière relance ${lastDate}`);
      // V4.47 — Recharge l'activité ET refresh parent (stats) pour MAJ visible sans reload
      await Promise.all([reloadActivity(), Promise.resolve(onContactStamped?.(name))]);
    } catch {
      toast.error("Erreur");
    } finally {
      setMarking(false);
    }
  };

  // V4.47 — Renommer l'apporteur (avec anti-doublon backend)
  const submitRename = async () => {
    const newName = (renameValue || "").trim();
    if (!newName || newName === name) {
      setRenaming(false);
      return;
    }
    try {
      await api.post(`/apporteurs/${encodeURIComponent(name)}/rename`, { new_name: newName });
      toast.success(`Renommé en ${newName}`);
      setRenaming(false);
      // V4.47 — Refresh global + repointage du drawer vers le nouveau nom (single source of truth parent)
      if (onRenamed) {
        await onRenamed(name, newName);
      } else {
        onContactStamped?.(newName);
        onSwitchApporteur?.(newName);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors du renommage";
      toast.error(typeof msg === "string" ? msg : "Erreur lors du renommage");
    }
  };

  // V4.47 — Supprimer l'apporteur (refusé si dossiers liés → message backend)
  const submitDelete = async () => {
    if (!name) return;
    setDeleting(true);
    try {
      await api.delete(`/apporteurs/${encodeURIComponent(name)}`);
      toast.success(`${name} supprimé`);
      onContactStamped?.();
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "has_dossiers") {
        toast.error(`${detail.message} (${detail.dossiers_count} dossier(s))`, { duration: 6000 });
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur lors de la suppression");
      }
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  if (!name) return null;

  const tier = apporteur?.tier;
  const tierStyle = TIER_STYLES[tier] || TIER_STYLES.C;

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40 z-[55] backdrop-blur-sm" onClick={onClose} />
      <aside
        className="fixed top-0 right-0 bottom-0 z-[60] w-full sm:w-[440px] bg-white shadow-2xl flex flex-col"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
        data-testid="apporteur-drawer"
      >
        <header className="px-4 pt-3 pb-3 border-b border-slate-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                <span>Apporteur</span>
                <span
                  className="px-1.5 py-0.5 rounded text-[9px] font-extrabold"
                  style={{ background: tierStyle.bg, color: tierStyle.color }}
                  data-testid="drawer-tier-badge"
                >
                  Tier {tierStyle.label}
                </span>
              </div>
              {renaming ? (
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="text"
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") submitRename(); if (e.key === "Escape") setRenaming(false); }}
                    className="flex-1 min-w-0 px-2 py-1.5 rounded-lg border border-slate-300 text-[16px] font-extrabold focus:outline-none focus:border-[color:var(--cf-primary)] focus:ring-1 focus:ring-[color:var(--cf-primary-border)]"
                    data-testid="apporteur-rename-input"
                  />
                  <button
                    type="button"
                    onClick={submitRename}
                    className="px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-[11.5px] font-extrabold"
                    data-testid="apporteur-rename-submit"
                  >
                    OK
                  </button>
                  <button
                    type="button"
                    onClick={() => setRenaming(false)}
                    className="px-2 py-1.5 rounded-lg text-slate-500 text-[11.5px] font-extrabold hover:bg-slate-100"
                    data-testid="apporteur-rename-cancel"
                  >
                    Annuler
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <h2 className="font-display text-xl font-extrabold text-slate-900 truncate" data-testid="drawer-name">{name}</h2>
                  <button
                    type="button"
                    onClick={() => { setRenameValue(name); setRenaming(true); }}
                    className="p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    title="Renommer l'apporteur"
                    data-testid="apporteur-rename-trigger"
                  >
                    <PencilSimple size={12} weight="bold" />
                  </button>
                </div>
              )}
              <ParrainInline
                currentParrain={data?.meta?.parrain || null}
                apporteurName={name}
                options={(allApporteurs || []).map((a) => a.name).filter(Boolean)}
                onSaved={() => { reloadMeta(); onContactStamped?.(); }}
                onNavigate={(parrainName) => onSwitchApporteur?.(parrainName)}
              />
              {data && (
                <div className="text-[11px] text-slate-500 mt-0.5 font-semibold">
                  {data.count} dossier{data.count > 1 ? "s" : ""} apportés
            </div>
              )}
              {name && <PartnerLinkBlock apporteurName={name} />}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 flex-shrink-0"
              data-testid="apporteur-drawer-close"
              aria-label="Fermer"
            >
              <X size={16} weight="bold" />
            </button>
          </div>

          {/* Relation row */}
          {apporteur && (
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]" data-testid="drawer-relation">
              <div className="bg-slate-50 rounded-lg px-2.5 py-2">
                <div className="text-[9px] font-bold uppercase text-slate-400 tracking-wider">Dernier contact</div>
                <div className="text-[12.5px] font-bold text-slate-800 mt-0.5">
                  {fmtRelativeDate(apporteur.last_contact_date)}
                  {apporteur.last_contact_source === "auto" && (
                    <span className="ml-1 text-[9px] font-bold text-slate-400 uppercase">auto</span>
                  )}
                </div>
              </div>
              <div
                className="rounded-lg px-2.5 py-2"
                style={{ background: apporteur.late ? "var(--cf-danger-soft)" : "var(--cf-bg-2)" }}
              >
                <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: apporteur.late ? "var(--cf-danger-text)" : "var(--cf-neutral-text)" }}>
                  Prochaine relance
                </div>
                <div
                  className="text-[12.5px] font-bold mt-0.5 flex items-center gap-1"
                  style={{ color: apporteur.late ? "var(--cf-danger-text)" : "var(--cf-text)" }}
                >
                  {apporteur.late && <Warning size={11} weight="fill" />}
                  {fmtRelance(apporteur.days_until_next, apporteur.late)}
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={markContacted}
            disabled={marking}
            className="mt-3 w-full py-2.5 rounded-xl bg-[color:var(--cf-primary)] text-white font-bold text-[13px] flex items-center justify-center gap-2 shadow-[0_4px_12px_rgba(45,110,255,0.25)] hover:bg-[color:var(--cf-primary-hover)] disabled:opacity-50"
            data-testid="apporteur-mark-contacted"
          >
            <ArrowClockwise size={14} weight="bold" /> {marking ? "..." : "Marquer contacté aujourd'hui"}
          </button>

          {/* V4.43 — Éditeur de segmentation Particulier/Pro */}
          {apporteur && (
            <div className="mt-3">
              <ApporteurSegmentationEditor
                apporteur={apporteur}
                onSaved={() => {
                  onContactStamped?.();
                }}
              />
            </div>
          )}

          {/* V4.47 — Journal d'activité (3 dernières entrées) */}
          {activity.length > 0 && (
            <div className="mt-3 border border-slate-200 rounded-xl p-2.5 bg-slate-50/40" data-testid="apporteur-activity-log">
              <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-1.5">
                <ClockClockwise size={11} weight="bold" /> Journal d&apos;activité
              </div>
              <ul className="space-y-1">
                {activity.slice(0, 3).map((a) => {
                  const labelMap = { relance: "Apporteur relancé", renamed: "Renommé", deleted: "Supprimé", merged: "Fusionné" };
                  const label = labelMap[a.event] || a.event;
                  const when = a.occurred_at ? new Date(a.occurred_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                  return (
                    <li key={a.id} className="text-[11px] font-semibold text-slate-600 flex items-start gap-1.5" data-testid={`activity-item-${a.event}`}>
                      <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-slate-400 flex-shrink-0" />
                      <span><b className="text-slate-800">{label}</b> — {when}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* V4.47 — Action Supprimer (refusée backend si dossiers liés) */}
          {apporteur && (
            <div className="mt-3 border-t border-slate-200 pt-3">
              {confirmDelete ? (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="apporteur-delete-confirm">
                  <div className="text-[12px] font-extrabold text-rose-900 mb-2">
                    Voulez-vous vraiment supprimer cet apporteur ?
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(false)}
                      disabled={deleting}
                      className="flex-1 px-3 py-1.5 rounded-lg text-[11px] font-extrabold text-slate-600 bg-white border border-slate-300 hover:bg-slate-50"
                      data-testid="apporteur-delete-cancel"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={submitDelete}
                      disabled={deleting}
                      className="flex-1 px-3 py-1.5 rounded-lg text-[11px] font-extrabold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-60 inline-flex items-center justify-center gap-1"
                      data-testid="apporteur-delete-confirm-submit"
                    >
                      <Trash size={11} weight="bold" />
                      {deleting ? "..." : "Supprimer"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="w-full px-3 py-1.5 rounded-lg text-[11px] font-extrabold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 inline-flex items-center justify-center gap-1.5"
                  data-testid="apporteur-delete-trigger"
                >
                  <Trash size={11} weight="bold" />
                  Supprimer l&apos;apporteur
                </button>
              )}
            </div>
          )}
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading && (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 bg-slate-50 rounded-lg animate-pulse" />
              ))}
            </div>
          )}
          {!loading && data?.dossiers?.length === 0 && (
            <div className="text-center text-xs text-slate-400 py-8">Aucun dossier.</div>
          )}
          {!loading && data?.dossiers?.map((d) => (
            <button
              key={d.id}
              onClick={() => { onClose(); navigate(`/dossier/${d.id}`); }}
              className="w-full text-left border border-slate-200 rounded-lg px-3 py-2.5 hover:border-slate-300 hover:shadow-sm flex items-center gap-2"
              data-testid={`drawer-dossier-${d.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span
                    className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded"
                    style={{
                      background: d.status === "signe" ? "var(--cf-success-soft)" : d.status === "perdu" ? "var(--cf-danger-soft)" : "var(--cf-bg-2)",
                      color: d.status === "signe" ? "var(--cf-success-text)" : d.status === "perdu" ? "var(--cf-danger-text)" : "var(--cf-neutral-text)",
                    }}
                  >
                    {d.status === "signe" ? "Signé" : d.status === "perdu" ? "Perdu" : d.etape}
                  </span>
                  <span className="text-[13px] font-bold text-slate-800 truncate">{d.nom}</span>
                </div>
                <div className="text-[10.5px] text-slate-500 mt-0.5 truncate">
                  CA net : {fmtMoney(d.ca_net || 0)}
                  {d.mandat_signe ? <span className="ml-1.5 text-emerald-600">· mandat</span> : null}
                </div>
              </div>
              <ArrowRight size={13} weight="bold" className="text-slate-400" />
            </button>
          ))}
        </div>
      </aside>
    </>
  );
}

/* ---------- Creation dialog ---------- */
function CreateApporteurDialog({ open, onClose, onCreated, existingNames }) {
  const [form, setForm] = useState({ name: "", parrain: "", commission_default: 500 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: "", parrain: "", commission_default: 500 });
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    setSubmitting(true);
    try {
      await api.post("/apporteurs", {
        name: form.name.trim(),
        parrain: form.parrain.trim() || null,
        commission_default: Number(form.commission_default) || 500,
      });
      toast.success("Apporteur créé");
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-slate-900/40 backdrop-blur-sm p-2">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" data-testid="create-apporteur-dialog">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h2 className="font-display text-lg font-extrabold text-slate-900">Nouvel apporteur</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100"
            data-testid="create-apporteur-close"
          >
            <X size={16} weight="bold" />
          </button>
        </header>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              Nom <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              autoFocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Prénom Nom"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-[14px] focus:border-[color:var(--cf-primary)] focus:outline-none"
              data-testid="create-apporteur-name"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              Parrain
            </label>
            <input
              type="text"
              list="apporteur-parrain-list"
              value={form.parrain}
              onChange={(e) => setForm({ ...form, parrain: e.target.value })}
              placeholder="Optionnel — choisir parmi les apporteurs existants"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-[14px] focus:border-[color:var(--cf-primary)] focus:outline-none"
              data-testid="create-apporteur-parrain"
            />
            <datalist id="apporteur-parrain-list">
              {(existingNames || []).map((n) => <option key={n} value={n} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              Commission par défaut (€)
            </label>
            <input
              type="number"
              value={form.commission_default}
              onChange={(e) => setForm({ ...form, commission_default: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-[14px] focus:border-[color:var(--cf-primary)] focus:outline-none"
              data-testid="create-apporteur-commission"
            />
          </div>
        </div>
        <footer className="px-5 py-3 border-t border-slate-100 flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm hover:bg-slate-200"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="flex-[2] py-2.5 rounded-xl bg-[color:var(--cf-primary)] text-white font-bold text-sm hover:bg-[color:var(--cf-primary-hover)] disabled:opacity-50"
            data-testid="create-apporteur-submit"
          >
            {submitting ? "Création..." : "Créer apporteur"}
          </button>
        </footer>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function Apporteurs() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("volume");
  const [drawerApporteur, setDrawerApporteur] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  // V4.16 — Recherche rapide par nom (insensible casse + accents partiels)
  const [searchQuery, setSearchQuery] = useState("");
  // V4.43 — Filtres de segmentation (catégorie + activité pro)
  const [categoryFilter, setCategoryFilter] = useState("all"); // all | professionnel | particulier | non_renseigne
  const [activityFilter, setActivityFilter] = useState("all"); // all | <business_activity key>

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/apporteurs/stats?sort=${sort}`);
      setData(res.data);
      return res.data;
    } catch {
      toast.error("Impossible de charger les apporteurs");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [sort]);

  // V4.16 — Filtrage temps réel par nom : insensible casse + accents, match partiel
  const _norm = (s) =>
    (s || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  const q = _norm(searchQuery.trim());

  // V4.43 — Filtre composé : catégorie → activité → nom
  const filterAll = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.filter((a) => {
      // Catégorie (avec fallback non_renseigne si champ absent)
      if (categoryFilter !== "all") {
        const cat = a.category || "non_renseigne";
        if (cat !== categoryFilter) return false;
      }
      // Activité (uniquement applicable aux Pros — affecte Tous/Pros)
      if (activityFilter !== "all") {
        if ((a.business_activity || null) !== activityFilter) return false;
      }
      // Nom
      if (q && !_norm(a.name).includes(q)) return false;
      return true;
    });
  };

  const filteredList = filterAll(data?.list);
  const filteredTopVolume = filterAll(data?.top_volume);
  const filteredTopClosing = filterAll(data?.top_closing);
  const isSearching = q.length > 0 || categoryFilter !== "all" || activityFilter !== "all";
  const noResults = isSearching && filteredList.length === 0;
  // L'activité n'a de sens que si la catégorie filtrée inclut des pros
  const activityFilterVisible = categoryFilter === "all" || categoryFilter === "professionnel";

  // V4.43 — Compteurs par catégorie (calculés depuis la liste complète)
  const categoryCounts = (data?.list || []).reduce((acc, a) => {
    const k = a.category || "non_renseigne";
    acc[k] = (acc[k] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    return acc;
  }, {});

  const onContactStamped = async () => {
    await fetchData();
    // Recharge l'apporteur affiché dans le drawer pour rafraîchir tier/relance
    if (drawerApporteur) {
      const fresh = data?.list?.find((a) => a.name === drawerApporteur.name);
      if (fresh) setDrawerApporteur(fresh);
    }
  };

  return (
    <Layout
      title="Apporteurs"
      subtitle={
        <span className="flex items-center gap-1.5">
          <UsersThree size={12} weight="fill" color="var(--cf-primary)" /> Pilotage réseau
        </span>
      }
      headerRight={
        <button
          onClick={() => setShowCreate(true)}
          className="w-9 h-9 rounded-full bg-[color:var(--cf-primary)] text-white flex items-center justify-center shadow-[0_4px_12px_rgba(45,110,255,0.3)]"
          data-testid="apporteur-add-btn"
          aria-label="Nouvel apporteur"
        >
          <Plus size={18} weight="bold" />
        </button>
      }
    >
      {/* Desktop "new" button */}
      <div className="hidden md:flex items-center justify-end mb-3">
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 bg-[color:var(--cf-primary)] hover:bg-[color:var(--cf-primary-hover)] text-white rounded-lg px-3 py-1.5 text-xs font-bold"
          data-testid="apporteur-add-desktop"
        >
          <Plus size={13} weight="bold" /> Nouvel apporteur
        </button>
      </div>
      {/* Totals row (desktop) */}
      {data?.totals && (
        <div className="hidden md:grid grid-cols-5 gap-3 mb-4">
          {[
            { label: "Apporteurs actifs", value: fmtInt(data.totals.apporteurs_count) },
            { label: "Dossiers apportés", value: fmtInt(data.totals.dossiers_total) },
            { label: "Signés", value: fmtInt(data.totals.signed_total) },
            { label: "CA net cumulé", value: fmtMoney(data.totals.ca_net_total) },
            {
              label: "À recontacter",
              value: fmtInt(data.totals.late_count),
              danger: (data.totals.late_count || 0) > 0,
            },
          ].map((k, i) => (
            <div
              key={i}
              className={`card-base p-3 ${k.danger ? "border-rose-200" : ""}`}
              data-testid={`apporteurs-total-${i}`}
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{k.label}</div>
              <div
                className="font-display text-xl font-extrabold mt-0.5 tabular-nums"
                style={{ color: k.danger ? "var(--cf-danger-text)" : "var(--cf-text)" }}
              >
                {k.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* V4.17 — Pilotage intelligent (5 blocs actionnables, en haut de page) */}
      {!loading && (
        <ApporteursInsights
          searchQuery={searchQuery}
          onOpenApporteur={(item) => {
            // Réutilise l'ouverture drawer existante : cherche dans la liste complète
            const found = (data?.list || []).find(
              (a) => a.name.toLowerCase() === (item.name || "").toLowerCase()
            );
            setDrawerApporteur(found || item);
          }}
          refreshTrigger={sort /* re-fetch when underlying data may change */}
          onContactStamped={fetchData}
        />
      )}

      {/* Podiums (filtrés par la recherche) */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <PodiumCard title="Top 5 volume" icon={Crown} color="var(--cf-warning)" items={filteredTopVolume} metric="volume" />
          <PodiumCard
            title="Top 5 closing"
            icon={TrendUp}
            color="var(--cf-success)"
            items={filteredTopClosing}
            metric="closing_rate"
            metricFmt={fmtPct}
          />
        </div>
      )}

      {/* V4.16 — Recherche rapide par nom (entre podiums et table) */}
      <div className="mb-3" data-testid="apporteurs-search-wrapper">
        <div className="relative">
          <MagnifyingGlass
            size={16}
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un apporteur…"
            className="w-full h-10 pl-9 pr-9 rounded-xl bg-white border border-slate-200 text-[13.5px] font-semibold text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[color:var(--cf-primary)] focus:ring-2 focus:ring-[color:var(--cf-primary-border)] transition-all"
            data-testid="apporteurs-search-input"
            aria-label="Rechercher un apporteur"
            autoComplete="off"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              data-testid="apporteurs-search-clear"
              aria-label="Effacer la recherche"
            >
              <X size={13} weight="bold" />
            </button>
          )}
        </div>
        {isSearching && !noResults && filteredList.length < (data?.list?.length || 0) && (
          <div className="mt-1.5 text-[11px] text-slate-400 font-semibold" data-testid="apporteurs-search-count">
            {filteredList.length} résultat{filteredList.length > 1 ? "s" : ""} sur {data?.list?.length || 0}
          </div>
        )}
      </div>

      {/* V4.43 — Filtres de segmentation (catégorie + activité pro) */}
      <div className="mb-3 space-y-2" data-testid="apporteurs-segmentation-filters">
        {/* Onglets catégorie */}
        <div className="flex items-center gap-1.5 flex-wrap" role="tablist" aria-label="Filtre catégorie">
          {[
            { key: "all", label: "Tous", icon: UsersThree, color: "var(--cf-text)" },
            { key: "professionnel", label: "Pros", icon: Briefcase, color: "var(--cf-primary-hover)" },
            { key: "particulier", label: "Particuliers", icon: UserIcon, color: "var(--cf-accent-text)" },
            { key: "non_renseigne", label: "Non renseignés", icon: Question, color: "var(--cf-text-2)" },
          ].map((f) => {
            const count = categoryCounts[f.key] || 0;
            const active = categoryFilter === f.key;
            const Icon = f.icon;
            return (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setCategoryFilter(f.key)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11.5px] font-extrabold border transition-colors ${
                  active ? "shadow-sm" : "hover:bg-slate-50"
                }`}
                style={{
                  background: active ? "var(--cf-text)" : "#FFFFFF",
                  color: active ? "#FFFFFF" : "var(--cf-neutral-text)",
                  borderColor: active ? "var(--cf-text)" : "var(--cf-border)",
                }}
                data-testid={`apporteur-filter-category-${f.key}`}
              >
                <Icon size={12} weight={active ? "fill" : "regular"} />
                <span>{f.label}</span>
                <span
                  className="px-1.5 py-0.5 rounded text-[9.5px] font-extrabold tabular-nums"
                  style={{
                    background: active ? "rgba(255,255,255,0.18)" : "var(--cf-bg-2)",
                    color: active ? "#FFFFFF" : "var(--cf-neutral-text)",
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Filtre activité — visible uniquement si la catégorie courante peut contenir des pros */}
        {activityFilterVisible && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="apporteurs-activity-filter">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Activité</span>
            <select
              value={activityFilter}
              onChange={(e) => setActivityFilter(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-slate-200 text-[11.5px] font-semibold bg-white focus:outline-none focus:border-[color:var(--cf-primary)] focus:ring-1 focus:ring-[color:var(--cf-primary-border)]"
              data-testid="apporteur-filter-activity"
            >
              <option value="all">Toutes</option>
              {BUSINESS_ACTIVITIES.map((b) => (
                <option key={b.key} value={b.key}>{b.label}</option>
              ))}
            </select>
            {(categoryFilter !== "all" || activityFilter !== "all") && (
              <button
                type="button"
                onClick={() => { setCategoryFilter("all"); setActivityFilter("all"); }}
                className="text-[10.5px] font-extrabold text-slate-500 hover:text-slate-900 underline underline-offset-2"
                data-testid="apporteur-filter-reset"
              >
                Réinitialiser
              </button>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card-base overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="text-[13px] font-extrabold text-slate-900 uppercase tracking-wider">Tous les apporteurs</h3>
          <div className="flex items-center gap-1">
            <span className="text-[10.5px] text-slate-500 font-semibold">Tri :</span>
            {SORTS.map((s) => (
              <button
                key={s.key}
                onClick={() => setSort(s.key)}
                className={`px-2 py-1 rounded-md text-[10.5px] font-bold ${
                  sort === s.key ? "bg-[color:var(--cf-primary-soft)] text-[color:var(--cf-primary)]" : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`apporteur-sort-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-6 space-y-2">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-12 bg-slate-50 rounded-lg animate-pulse" />)}
          </div>
        ) : !data?.list?.length ? (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="apporteurs-empty">
            Aucun apporteur — renseigne le champ "apporteur" sur tes dossiers pour piloter ton réseau.
          </div>
        ) : noResults ? (
          <div className="p-8 text-center text-sm text-slate-500 font-semibold" data-testid="apporteurs-no-results">
            Aucun apporteur trouvé
            <div className="text-xs text-slate-400 font-semibold mt-1">pour « {searchQuery} »</div>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <table className="hidden md:table w-full text-[12.5px]" data-testid="apporteurs-table">
              <thead>
                <tr className="text-left text-[10.5px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="px-3 py-2.5">Tier</th>
                  <th className="px-2 py-2.5">Nom · Segment</th>
                  <th className="px-2 py-2.5 text-right">Vol.</th>
                  <th className="px-2 py-2.5 text-right">Signés</th>
                  <th className="px-2 py-2.5 text-right">Closing</th>
                  <th className="px-2 py-2.5 text-right">CA net</th>
                  <th className="px-2 py-2.5">Dernier contact</th>
                  <th className="px-2 py-2.5">Prochaine relance</th>
                  <th className="px-2 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((a) => (
                  <tr
                    key={a.name}
                    onClick={() => setDrawerApporteur(a)}
                    className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${a.late ? "bg-rose-50/30" : ""}`}
                    data-testid={`apporteur-row-${a.name}`}
                  >
                    <td className="px-3 py-2.5"><TierBadge tier={a.tier} /></td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-bold text-slate-800">{a.name}</span>
                        <CategoryBadge category={a.category} />
                      </div>
                      {(a.category === "professionnel") && (a.business_activity || a.company_name) && (
                        <div className="text-[10.5px] text-slate-500 font-semibold mt-0.5 truncate max-w-[260px]">
                          {[
                            a.business_activity ? (BUSINESS_ACTIVITY_LABELS[a.business_activity] || a.business_activity) : null,
                            a.company_name,
                          ].filter(Boolean).join(" · ")}
                        </div>
                      )}
                      {a.category === "particulier" && a.personal_relation && (
                        <div className="text-[10.5px] text-slate-500 font-semibold mt-0.5 truncate max-w-[260px]">
                          {a.personal_relation === "autre" && a.personal_relation_other
                            ? a.personal_relation_other
                            : (PERSONAL_RELATION_LABELS[a.personal_relation] || a.personal_relation)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{fmtInt(a.volume)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums font-bold text-emerald-700">{fmtInt(a.signed)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums font-bold">{fmtPct(a.closing_rate)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums font-bold">{fmtMoney(a.ca_net_total)}</td>
                    <td className="px-2 py-2.5 text-slate-600">
                      {fmtRelativeDate(a.last_contact_date)}
                      {a.last_contact_source === "auto" && <span className="ml-1 text-[9px] text-slate-400 font-bold uppercase">auto</span>}
                    </td>
                    <td className="px-2 py-2.5">
                      <span
                        className="font-bold inline-flex items-center gap-1"
                        style={{ color: a.late ? "var(--cf-danger-text)" : "var(--cf-neutral-text)" }}
                      >
                        {a.late && <Warning size={11} weight="fill" />}
                        {fmtRelance(a.days_until_next, a.late)}
                      </span>
                    </td>
                    <td className="px-2 py-2.5 text-right text-slate-400"><ArrowRight size={12} weight="bold" /></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile list */}
            <div className="md:hidden divide-y divide-slate-100" data-testid="apporteurs-mobile-list">
              {filteredList.map((a) => (
                <button
                  key={a.name}
                  onClick={() => setDrawerApporteur(a)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${a.late ? "bg-rose-50/30" : ""}`}
                  data-testid={`apporteur-row-mobile-${a.name}`}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <TierBadge tier={a.tier} />
                    <span className="font-bold text-[14px] text-slate-900 flex-1 truncate">{a.name}</span>
                    <CategoryBadge category={a.category} />
                    <span className="text-[12px] font-extrabold text-emerald-700 tabular-nums">{fmtMoney(a.ca_net_total)}</span>
                  </div>
                  {(a.category === "professionnel" && (a.business_activity || a.company_name)) && (
                    <div className="text-[10.5px] text-slate-500 font-semibold mb-0.5 truncate">
                      {[
                        a.business_activity ? (BUSINESS_ACTIVITY_LABELS[a.business_activity] || a.business_activity) : null,
                        a.company_name,
                      ].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  {a.category === "particulier" && a.personal_relation && (
                    <div className="text-[10.5px] text-slate-500 font-semibold mb-0.5 truncate">
                      {a.personal_relation === "autre" && a.personal_relation_other
                        ? a.personal_relation_other
                        : (PERSONAL_RELATION_LABELS[a.personal_relation] || a.personal_relation)}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                    <span><b className="text-slate-700">{a.volume}</b> dossiers</span>
                    <span>·</span>
                    <span><b className="text-emerald-700">{a.signed}</b> signés</span>
                    {a.closing_rate !== null && <><span>·</span><span><b>{fmtPct(a.closing_rate)}</b></span></>}
                  </div>
                  <div
                    className="text-[11px] mt-1 flex items-center gap-1.5 font-semibold"
                    style={{ color: a.late ? "var(--cf-danger-text)" : "var(--cf-text-2)" }}
                  >
                    {a.late && <Warning size={10} weight="fill" />}
                    Relance {fmtRelance(a.days_until_next, a.late)}
                    {a.last_contact_date && <span className="text-slate-400">· dernier {fmtRelativeDate(a.last_contact_date)}</span>}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <ApporteurDrawer
        apporteur={drawerApporteur}
        onClose={() => setDrawerApporteur(null)}
        onContactStamped={onContactStamped}
        onSwitchApporteur={(parrainName) => {
          // Cherche le parrain dans la liste, sinon ouvre une fiche minimale
          const found = (data?.list || []).find((a) => a.name.toLowerCase() === parrainName.toLowerCase());
          setDrawerApporteur(found || { name: parrainName, tier: "C" });
        }}
        onRenamed={async (oldName, newName) => {
          // V4.47 — Recharger la liste puis repointer le drawer vers le NOUVEAU nom
          // pour que drawer-name + dossiers liés s'affichent sous le nouveau nom
          // sans nécessiter de fermeture/réouverture manuelle.
          const fresh = await fetchData();
          const list = fresh?.list || [];
          const updated = list.find((a) => a.name.toLowerCase() === newName.toLowerCase());
          // Si l'apporteur n'est pas (encore) dans la liste filtrée, fallback sur stub minimal
          setDrawerApporteur(updated || { name: newName, tier: drawerApporteur?.tier || "C" });
        }}
        allApporteurs={data?.list || []}
      />
      <CreateApporteurDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={fetchData}
        existingNames={(data?.list || []).map((a) => a.name)}
      />
    </Layout>
  );
}
